/**
 * EnhancedGameWorld - Working 3D game with scene transitions and multiplayer
 * 
 * Based on SimpleGameWorld architecture (proven stable) with added features:
 * - Multiple scenes (Thornhaven exterior, Golden Mead Hall interior)
 * - Scene transitions via door interactions
 * - Spawn point system
 * - Mobile controls
 * - Multiplayer support (WebSocket-based)
 * - First-time tutorial system
 * 
 * PHASE 3: Open-World Foundation
 * - World coordinate system (worldId: 'midgard')
 * - Chunk streaming for terrain/objects
 * - Continuous travel (Thornhaven → Standing Stones)
 */

import { Suspense, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { Physics, RigidBody, CuboidCollider, CapsuleCollider } from '@react-three/rapier';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import * as THREE from 'three';
import { ArrowLeft, AlertTriangle, Hand, Zap, MapPin, Wifi, WifiOff, Loader, MessageSquare, Users, Settings } from 'lucide-react';

// Import new visual components
import HumanoidCharacter from './character/HumanoidCharacter';
import ThornhavenScene from './environment/ThornhavenScene';
import GoldenMeadHallInterior from './environment/GoldenMeadHallInterior';
import ThornhavenSmallHouse from './environment/ThornhavenSmallHouse';
import ThornhavenPerimeter from './environment/ThornhavenPerimeter';
import ThornhavenTownSquare from './environment/ThornhavenTownSquare';
import WorldObjectsLayer from './worldbuilder/WorldObjectsLayer';
import WorldBuilderPanel from './worldbuilder/WorldBuilderPanel';
import { WORLD_BUILDER_EMAIL } from '../../config/worldBuilder';
import SmallHouseInterior from './environment/SmallHouseInterior';
import ChunkStreamer from './environment/ChunkStreamer';
import api from '../../lib/api';
import { RemotePlayersContainer } from './RemotePlayer';
import GameTutorial, { hasSeenTutorial, ContextualHint } from './GameTutorial';
import GameChat from './GameChat';
import GameSettings, { loadSettings, saveSettings, CAMERA_MODES } from './GameSettings';
import { PerformanceStatsCollector, PerformanceOverlayUI, useWsMessageRate } from './PerformanceOverlay';
import VoiceControls from './VoiceControls';
import GameHUD from './GameHUD';
import Minimap from './Minimap';
import GameMenu from './GameMenu';

// Import ecctrl-based player controller
import EcctrlPlayer, { EcctrlKeyboardWrapper, KEYBOARD_MAP } from './EcctrlPlayer';
import NorseMobileControls, { useIsMobile } from './NorseMobileControls';
import { useInputState, useIsInputBlocked, useMenuInputCapture, useChatInputCapture, GameInputProvider } from './GameInputManager';
import { KeyboardControls } from '@react-three/drei';

// Import multiplayer service
import multiplayerService, { ConnectionState } from '../../services/MultiplayerService';

// Import social zone service
import { sitAtZone, standUp, emitJoinZone, emitLeaveZone, getZoneTypeLabel } from '../../services/SocialZoneService';

// Import auth context
import { useAuth } from '../../context/AuthContext';

// Import world data (Phase 3)
import { 
  WORLD_CONFIG, 
  WORLD_SPAWN_POINTS, 
  INTERIOR_SCENES,
  getWorldSpawnPoint,
  isInInterior 
} from '../../config/midgardData';

// ========================
// GRAPHICS QUALITY PROFILES
// ========================
const GRAPHICS_PROFILES = {
  performance: {
    dpr: 1.0,
    shadowMapSize: 512,
    shadowEnabled: false,
    antialias: false,
  },
  balanced: {
    dpr: 1.25,
    shadowMapSize: 1024,
    shadowEnabled: true,
    antialias: true,
  },
  quality: {
    dpr: 2.0,
    shadowMapSize: 2048,
    shadowEnabled: true,
    antialias: true,
  },
  auto: null, // Determined at runtime
};

// Detect appropriate auto profile based on device
function getAutoProfile() {
  const isMobile = /Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const nativeDpr = window.devicePixelRatio || 1;
  
  if (isMobile) {
    // Mobile: cap DPR aggressively
    return {
      dpr: Math.min(nativeDpr, 1.5),
      shadowMapSize: 512,
      shadowEnabled: true,
      antialias: false,
    };
  } else {
    // Desktop: balanced settings
    return {
      dpr: Math.min(nativeDpr, 2),
      shadowMapSize: 1024,
      shadowEnabled: true,
      antialias: true,
    };
  }
}

// Get saved graphics preference or auto
function getSavedGraphicsProfile() {
  const saved = localStorage.getItem('midgard_graphics_profile');
  if (saved && GRAPHICS_PROFILES[saved]) {
    return saved;
  }
  return 'auto';
}

// ========================
// INTERACTION PRIORITY CONFIG
// ========================
// Priority order: Higher number = higher priority
const INTERACTION_PRIORITY = {
  talk: 100,    // Talk to player (highest - direct person interaction)
  stand: 90,    // Stand up (when seated)
  sit: 80,      // Sit at social zone
  door: 70,     // Enter/Exit scenes
  interact: 50, // Generic interactions
};

// Interaction radius for talking to players
const TALK_RADIUS = 3.5;

// ========================
// SPAWN POINTS & SCENE DATA
// Phase 3: Now supports both interior scenes and world-based exterior
// ========================

const SPAWN_POINTS = {
  // World exterior spawn points (Phase 3)
  'midgard-world': {
    default: { x: 0, y: 1, z: 25 },
    goldenMeadHallExit: { x: 17, y: 1, z: 0 },
    fastTravel: { x: 0, y: 1, z: 25 },
    standingStones: { x: 0, y: 1, z: -175 },
    smallHouseExit: { x: -14, y: 1, z: 11 },
  },
  // Legacy scene ID mapping (for backwards compatibility)
  'thornhaven-exterior': {
    default: { x: 0, y: 1, z: 25 },
    goldenMeadHallExit: { x: 17, y: 1, z: 0 },
    fastTravel: { x: 0, y: 1, z: 25 },
  },
  'golden-mead-hall': {
    entrance: { x: 0, y: 1, z: 6 },
    fastTravel: { x: 0, y: 1, z: 0 },
  },
  // Phase 6E: first functional dwelling interior (prop_def_thornhaven_small_house)
  'thornhaven-small-house': {
    default: { x: 0, y: 1, z: 2 },
    entrance: { x: 0, y: 1, z: 2 },
  },
};

const SCENES = {
  // Phase 3: World exterior (continuous space)
  'midgard-world': {
    name: 'Midgard',
    type: 'world',
    worldId: 'midgard',
    skyColor: '#87CEEB',
    groundColor: '#4a7c4f',
  },
  // Legacy exterior (redirects to world)
  'thornhaven-exterior': {
    name: 'Thornhaven',
    type: 'exterior',  // Legacy, treated as world
    worldId: 'midgard',
    skyColor: '#87CEEB',
    groundColor: '#4a7c4f',
  },
  'golden-mead-hall': {
    name: 'The Golden Mead Hall',
    type: 'interior',
    skyColor: '#1a0f0a',
    groundColor: '#3E2723',
  },
  // Phase 6E: first functional dwelling interior
  'thornhaven-small-house': {
    name: 'Small House',
    type: 'interior',
    skyColor: '#140d08',
    groundColor: '#5d4030',
  },
};

// Phase 3: Interactions now work in world coordinates
const INTERACTIONS = {
  // World exterior interactions (Phase 3)
  'midgard-world': [
    {
      id: 'tavern-entrance',
      type: 'door',
      priority: INTERACTION_PRIORITY.door,
      position: { x: 20, z: 0 },
      radius: 3,
      prompt: 'Enter The Golden Mead Hall',
      action: { type: 'enterScene', targetScene: 'golden-mead-hall', spawnPoint: 'entrance' },
    },
  ],
  // Legacy exterior ID (same interactions)
  'thornhaven-exterior': [
    {
      id: 'tavern-entrance',
      type: 'door',
      priority: INTERACTION_PRIORITY.door,
      position: { x: 20, z: 0 },
      radius: 3,
      prompt: 'Enter The Golden Mead Hall',
      action: { type: 'enterScene', targetScene: 'golden-mead-hall', spawnPoint: 'entrance' },
    },
  ],
  'golden-mead-hall': [
    {
      id: 'tavern-exit',
      type: 'door',
      priority: INTERACTION_PRIORITY.door,
      position: { x: 0, z: 7.5 },
      radius: 2.5,
      prompt: 'Exit to Thornhaven',
      // Phase 3: Exit to world, not legacy scene
      action: { type: 'exitToWorld', spawnPoint: 'goldenMeadHallExit' },
    },
    // Social zone: Bar - positioned where player approaches from front
    {
      id: 'social-bar',
      type: 'socialZone',
      priority: INTERACTION_PRIORITY.sit,
      position: { x: 0, z: -5.5 },  // Moved forward for better accessibility
      radius: 3.0,  // Increased radius
      prompt: 'Sit at Bar',
      zoneId: 'golden-mead-hall-bar',
      zoneLabel: 'The Bar',
      action: { type: 'sitAtZone', zoneId: 'golden-mead-hall-bar' },
    },
    // Social zone: Hearth - central fire area
    {
      id: 'social-hearth',
      type: 'socialZone',
      priority: INTERACTION_PRIORITY.sit,
      position: { x: 0, z: -4 },  // Moved forward slightly
      radius: 2.5,
      prompt: 'Sit by Hearth',
      zoneId: 'golden-mead-hall-hearth',
      zoneLabel: 'The Hearth',
      action: { type: 'sitAtZone', zoneId: 'golden-mead-hall-hearth' },
    },
    // Social zone: Table 1 (left side)
    {
      id: 'social-table1',
      type: 'socialZone',
      priority: INTERACTION_PRIORITY.sit,
      position: { x: -3.5, z: -2 },
      radius: 3.0,  // Increased radius
      prompt: 'Sit at Table',
      zoneId: 'golden-mead-hall-table-01',
      zoneLabel: 'Table 1',
      action: { type: 'sitAtZone', zoneId: 'golden-mead-hall-table-01' },
    },
  ],
  // Phase 6E: inside the first dwelling. Exit resolves via /api/properties/exit.
  'thornhaven-small-house': [
    {
      id: 'small-house-exit',
      type: 'door',
      priority: INTERACTION_PRIORITY.door,
      position: { x: 0, z: 3.2 },
      radius: 2.5,
      prompt: 'Exit to Thornhaven',
      action: { type: 'exitProperty' },
    },
  ],
};

// ========================
// PLAYER COMPONENT (with Humanoid Character and Third-Person Camera)
// ========================

// Camera configuration base (can be overridden by settings)
const CAMERA_CONFIG_BASE = {
  minPitch: -0.2,        // Min vertical angle (radians)
  maxPitch: 1.0,         // Max vertical angle (radians)
  rotationSpeed: 0.003,  // Base mouse rotation sensitivity
  touchRotationSpeed: 0.006, // Base touch rotation sensitivity
  followLerp: 0.08,      // How smoothly camera follows
  lookAtOffset: 1.2,     // Look at point height offset
};

// Camera mode configurations
const CAMERA_MODE_CONFIG = {
  firstPerson: { distance: 0.1, height: 1.7, lookAtOffset: 0 },
  thirdPersonNear: { distance: 4, height: 2.5, lookAtOffset: 1.0 },
  thirdPersonNormal: { distance: 8, height: 4, lookAtOffset: 1.2 },
  thirdPersonFar: { distance: 14, height: 6, lookAtOffset: 1.5 },
};

function Player({ 
  initialPosition, 
  sceneId, 
  onInteractionChange, 
  onTriggerInteraction,
  onPositionUpdate,
  onNearbyPlayersChange,
  mobileInput,
  mobileCameraDelta,
  isSeated = false,
  seatPosition = null,
  seatRotation = 0,
  remotePlayers = [],
  gameSettings = {},
}) {
  const rigidBodyRef = useRef();
  const { camera, gl } = useThree();
  const keys = useRef({ w: false, a: false, s: false, d: false, shift: false, e: false, space: false });
  const currentInteraction = useRef(null);
  const wasEPressed = useRef(false);
  const lastPositionUpdate = useRef(0);
  
  // Jump state
  const isJumping = useRef(false);
  const canJump = useRef(true);
  
  // Character rotation state (for smooth rotation toward movement)
  const characterRotation = useRef(0);
  const targetCharacterRotation = useRef(0);
  
  // Ref-based position for smooth visual updates EVERY FRAME
  // This is the authoritative local visual position - updated from physics body each frame
  const visualPositionRef = useRef([initialPosition.x, 0, initialPosition.z]);
  
  // Animation state (can be throttled for React, but we use refs for actual animation)
  const animationStateRef = useRef({
    isMoving: false,
    isRunning: false,
    movementDirection: { x: 0, z: 0 },
  });
  
  // React state for UI purposes only (rarely updated)
  const [animationState, setAnimationState] = useState({
    isMoving: false,
    isRunning: false,
  });
  
  // Throttled animation state update for React (UI indicators only)
  const lastAnimStateUpdate = useRef(0);
  const updateAnimationState = useCallback((isMoving, isRunning, direction) => {
    // Always update refs immediately for smooth animation
    animationStateRef.current = { isMoving, isRunning, movementDirection: direction };
    
    // Throttle React state updates to 200ms for UI purposes
    const now = Date.now();
    if (now - lastAnimStateUpdate.current > 200) {
      lastAnimStateUpdate.current = now;
      setAnimationState(prev => {
        if (prev.isMoving !== isMoving || prev.isRunning !== isRunning) {
          return { isMoving, isRunning };
        }
        return prev;
      });
    }
  }, []);
  
  // Camera orbit state
  const cameraOrbit = useRef({ yaw: 0, pitch: 0.4 });
  const isDragging = useRef(false);
  const lastPointer = useRef({ x: 0, y: 0 });
  const touchId = useRef(null);
  const smoothTarget = useRef(new THREE.Vector3(initialPosition.x, initialPosition.y, initialPosition.z));
  
  // Camera distance state (for scroll zoom)
  const cameraDistance = useRef(CAMERA_MODE_CONFIG.thirdPersonNormal.distance);
  const targetCameraDistance = useRef(CAMERA_MODE_CONFIG.thirdPersonNormal.distance);
  
  // Get camera config based on settings
  const getCameraConfig = useCallback(() => {
    const mode = gameSettings.cameraMode || 'thirdPersonNormal';
    const modeConfig = CAMERA_MODE_CONFIG[mode] || CAMERA_MODE_CONFIG.thirdPersonNormal;
    const sensitivity = gameSettings.cameraSensitivity || 1.0;
    const invertY = gameSettings.invertY || false;
    
    return {
      ...CAMERA_CONFIG_BASE,
      ...modeConfig,
      rotationSpeed: CAMERA_CONFIG_BASE.rotationSpeed * sensitivity,
      touchRotationSpeed: CAMERA_CONFIG_BASE.touchRotationSpeed * sensitivity,
      invertY,
      isFirstPerson: mode === 'firstPerson',
    };
  }, [gameSettings.cameraMode, gameSettings.cameraSensitivity, gameSettings.invertY]);
  
  // Update camera distance when mode changes
  useEffect(() => {
    const config = getCameraConfig();
    targetCameraDistance.current = config.distance;
  }, [getCameraConfig]);
  
  // Keyboard controls
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.code === 'KeyW') keys.current.w = true;
      if (e.code === 'KeyA') keys.current.a = true;
      if (e.code === 'KeyS') keys.current.s = true;
      if (e.code === 'KeyD') keys.current.d = true;
      if (e.code === 'ShiftLeft' || e.code === 'ShiftRight') keys.current.shift = true;
      if (e.code === 'KeyE') keys.current.e = true;
      if (e.code === 'Space') {
        keys.current.space = true;
        e.preventDefault(); // Prevent page scroll
      }
    };
    
    const handleKeyUp = (e) => {
      if (e.code === 'KeyW') keys.current.w = false;
      if (e.code === 'KeyA') keys.current.a = false;
      if (e.code === 'KeyS') keys.current.s = false;
      if (e.code === 'KeyD') keys.current.d = false;
      if (e.code === 'ShiftLeft' || e.code === 'ShiftRight') keys.current.shift = false;
      if (e.code === 'KeyE') keys.current.e = false;
      if (e.code === 'Space') keys.current.space = false;
    };
    
    // Left-click for action/attack
    const handleMouseDown = (e) => {
      if (e.button === 0) { // Left click
        // Emit generic action event (no combat logic yet)
        console.log('[Player] Action triggered');
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('mousedown', handleMouseDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('mousedown', handleMouseDown);
    };
  }, []);
  
  // Camera mouse/touch controls
  useEffect(() => {
    const canvas = gl.domElement;
    
    const handleMouseDown = (e) => {
      // Right mouse button for camera rotation
      if (e.button === 2 || e.button === 1) {
        isDragging.current = true;
        lastPointer.current = { x: e.clientX, y: e.clientY };
        e.preventDefault();
      }
    };
    
    const handleMouseMove = (e) => {
      if (!isDragging.current) return;
      
      const config = getCameraConfig();
      const deltaX = e.clientX - lastPointer.current.x;
      const deltaY = e.clientY - lastPointer.current.y;
      
      cameraOrbit.current.yaw -= deltaX * config.rotationSpeed;
      const pitchDelta = deltaY * config.rotationSpeed * (config.invertY ? -1 : 1);
      cameraOrbit.current.pitch -= pitchDelta;
      cameraOrbit.current.pitch = Math.max(CAMERA_CONFIG_BASE.minPitch, Math.min(CAMERA_CONFIG_BASE.maxPitch, cameraOrbit.current.pitch));
      
      lastPointer.current = { x: e.clientX, y: e.clientY };
    };
    
    const handleMouseUp = () => { isDragging.current = false; };
    const handleContextMenu = (e) => { e.preventDefault(); };
    
    // Scroll wheel for camera distance (zoom)
    const handleWheel = (e) => {
      e.preventDefault();
      const zoomSpeed = 0.5;
      const delta = e.deltaY > 0 ? zoomSpeed : -zoomSpeed;
      
      // Adjust target distance
      targetCameraDistance.current = Math.max(0.1, Math.min(20, targetCameraDistance.current + delta));
    };
    
    // Two-finger touch for camera rotation on mobile
    const handleTouchStart = (e) => {
      if (e.touches.length === 2) {
        isDragging.current = true;
        const touch = e.touches[1];
        touchId.current = touch.identifier;
        lastPointer.current = { x: touch.clientX, y: touch.clientY };
      }
    };
    
    const handleTouchMove = (e) => {
      if (!isDragging.current || touchId.current === null) return;
      const touch = Array.from(e.touches).find(t => t.identifier === touchId.current);
      if (!touch) return;
      
      const config = getCameraConfig();
      const deltaX = touch.clientX - lastPointer.current.x;
      const deltaY = touch.clientY - lastPointer.current.y;
      
      cameraOrbit.current.yaw -= deltaX * config.touchRotationSpeed;
      const pitchDelta = deltaY * config.touchRotationSpeed * (config.invertY ? -1 : 1);
      cameraOrbit.current.pitch -= pitchDelta;
      cameraOrbit.current.pitch = Math.max(CAMERA_CONFIG_BASE.minPitch, Math.min(CAMERA_CONFIG_BASE.maxPitch, cameraOrbit.current.pitch));
      
      lastPointer.current = { x: touch.clientX, y: touch.clientY };
    };
    
    const handleTouchEnd = (e) => {
      if (e.touches.length < 2) {
        isDragging.current = false;
        touchId.current = null;
      }
    };
    
    canvas.addEventListener('mousedown', handleMouseDown);
    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseup', handleMouseUp);
    canvas.addEventListener('mouseleave', handleMouseUp);
    canvas.addEventListener('contextmenu', handleContextMenu);
    canvas.addEventListener('wheel', handleWheel, { passive: false });
    canvas.addEventListener('touchstart', handleTouchStart, { passive: true });
    canvas.addEventListener('touchmove', handleTouchMove, { passive: true });
    canvas.addEventListener('touchend', handleTouchEnd);
    
    return () => {
      canvas.removeEventListener('mousedown', handleMouseDown);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseup', handleMouseUp);
      canvas.removeEventListener('mouseleave', handleMouseUp);
      canvas.removeEventListener('contextmenu', handleContextMenu);
      canvas.removeEventListener('wheel', handleWheel);
      canvas.removeEventListener('contextmenu', handleContextMenu);
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
    };
  }, [gl]);
  
  useFrame((state, delta) => {
    if (!rigidBodyRef.current) return;
    
    const rb = rigidBodyRef.current;
    const pos = rb.translation();
    
    // If seated, disable movement and snap to seat position
    if (isSeated && seatPosition) {
      rb.setTranslation({ x: seatPosition.x, y: seatPosition.y, z: seatPosition.z }, true);
      rb.setLinvel({ x: 0, y: 0, z: 0 }, true);
      
      // Update visual position for seated state
      visualPositionRef.current = [seatPosition.x, 0, seatPosition.z];
      
      // Update animation state to seated (not moving)
      if (animationStateRef.current.isMoving) {
        updateAnimationState(false, false, { x: 0, z: 0 });
      }
      
      // Get dynamic camera config
      const camConfig = getCameraConfig();
      
      // Smoothly interpolate camera distance
      cameraDistance.current += (targetCameraDistance.current - cameraDistance.current) * 0.1;
      const effectiveDistance = camConfig.isFirstPerson ? 0.1 : cameraDistance.current;
      
      // Still update camera to follow seated player
      smoothTarget.current.lerp(
        new THREE.Vector3(seatPosition.x, seatPosition.y, seatPosition.z),
        CAMERA_CONFIG_BASE.followLerp
      );
      
      const { yaw, pitch } = cameraOrbit.current;
      
      // First person: camera at eye level
      if (camConfig.isFirstPerson) {
        camera.position.set(
          smoothTarget.current.x,
          smoothTarget.current.y + camConfig.height,
          smoothTarget.current.z
        );
        // Look in the direction player is facing (use seat rotation)
        const lookDist = 5;
        camera.lookAt(
          smoothTarget.current.x + Math.sin(seatRotation) * lookDist,
          smoothTarget.current.y + camConfig.height,
          smoothTarget.current.z + Math.cos(seatRotation) * lookDist
        );
      } else {
        const camX = smoothTarget.current.x + Math.sin(yaw) * Math.cos(pitch) * effectiveDistance;
        const camY = smoothTarget.current.y + camConfig.height + Math.sin(pitch) * effectiveDistance * 0.5;
        const camZ = smoothTarget.current.z + Math.cos(yaw) * Math.cos(pitch) * effectiveDistance;
        
        camera.position.set(camX, camY, camZ);
        camera.lookAt(
          smoothTarget.current.x,
          smoothTarget.current.y + camConfig.lookAtOffset,
          smoothTarget.current.z
        );
      }
      
      // Report position
      if (onPositionUpdate) {
        onPositionUpdate(seatPosition, seatRotation, 'seated');
      }
      return;
    }
    
    // Get dynamic camera config
    const camConfig = getCameraConfig();
    
    // Apply mobile camera delta (from MobileControls right-side drag)
    if (mobileCameraDelta && (mobileCameraDelta.x !== 0 || mobileCameraDelta.y !== 0)) {
      cameraOrbit.current.yaw -= mobileCameraDelta.x;
      const pitchDelta = mobileCameraDelta.y * (camConfig.invertY ? -1 : 1);
      cameraOrbit.current.pitch -= pitchDelta;
      cameraOrbit.current.pitch = Math.max(CAMERA_CONFIG_BASE.minPitch, Math.min(CAMERA_CONFIG_BASE.maxPitch, cameraOrbit.current.pitch));
    }
    
    // Calculate raw input from keyboard or mobile
    let rawInputX = 0, rawInputZ = 0;
    let isSprinting = false;
    let wantsJump = false;
    
    if (keys.current.w || keys.current.a || keys.current.s || keys.current.d) {
      if (keys.current.w) rawInputZ -= 1;
      if (keys.current.s) rawInputZ += 1;
      if (keys.current.a) rawInputX -= 1;
      if (keys.current.d) rawInputX += 1;
      isSprinting = keys.current.shift;
    } else if (mobileInput.active) {
      rawInputX = mobileInput.x;
      rawInputZ = mobileInput.y;
      isSprinting = mobileInput.sprint;
    }
    
    // Check for jump input (Space key or mobile jump)
    wantsJump = keys.current.space || mobileInput.jump;
    
    // Convert to camera-relative direction
    const { yaw } = cameraOrbit.current;
    const cos = Math.cos(yaw);
    const sin = Math.sin(yaw);
    const moveX = rawInputX * cos - rawInputZ * sin;
    const moveZ = rawInputX * sin + rawInputZ * cos;
    
    // Normalize diagonal movement
    const magnitude = Math.sqrt(moveX * moveX + moveZ * moveZ);
    const normalizedX = magnitude > 0 ? moveX / magnitude : 0;
    const normalizedZ = magnitude > 0 ? moveZ / magnitude : 0;
    
    const hasMovement = magnitude > 0.1;
    const speed = isSprinting ? 10 : 5;
    
    // Get current velocity
    const currentVel = rb.linvel();
    
    // Ground detection (simple check: if Y velocity is near zero and position is near ground)
    const isGrounded = Math.abs(currentVel.y) < 0.5 && pos.y < 2;
    
    // Reset jump ability when grounded
    if (isGrounded && !isJumping.current) {
      canJump.current = true;
    }
    
    // Handle jump
    if (wantsJump && canJump.current && isGrounded) {
      const jumpForce = 6;
      rb.setLinvel({ 
        x: currentVel.x, 
        y: jumpForce, 
        z: currentVel.z 
      }, true);
      isJumping.current = true;
      canJump.current = false;
      
      // Reset jumping state after a short delay
      setTimeout(() => {
        isJumping.current = false;
      }, 100);
    }
    
    // Apply horizontal velocity with proper damping
    if (hasMovement) {
      // Smooth acceleration toward target velocity
      const targetVelX = normalizedX * magnitude * speed;
      const targetVelZ = normalizedZ * magnitude * speed;
      const lerpFactor = 0.2; // Smoothing factor
      
      rb.setLinvel({ 
        x: currentVel.x + (targetVelX - currentVel.x) * lerpFactor, 
        y: currentVel.y, 
        z: currentVel.z + (targetVelZ - currentVel.z) * lerpFactor 
      }, true);
      
      // Update target character rotation (face movement direction)
      targetCharacterRotation.current = Math.atan2(normalizedX * magnitude, normalizedZ * magnitude);
    } else {
      // Smooth deceleration when no input (no ice sliding)
      const decelFactor = 0.15;
      rb.setLinvel({ 
        x: currentVel.x * decelFactor, 
        y: currentVel.y, 
        z: currentVel.z * decelFactor 
      }, true);
    }
    
    // Smooth character rotation toward movement direction
    const rotDiff = targetCharacterRotation.current - characterRotation.current;
    // Handle wrap-around
    let adjustedDiff = rotDiff;
    if (rotDiff > Math.PI) adjustedDiff -= Math.PI * 2;
    if (rotDiff < -Math.PI) adjustedDiff += Math.PI * 2;
    characterRotation.current += adjustedDiff * 0.15;
    
    // *** CRITICAL: Update visual position ref EVERY FRAME for smooth local movement ***
    // Mutate in place to avoid array reallocation / GC pressure
    visualPositionRef.current[0] = pos.x;
    visualPositionRef.current[1] = 0;
    visualPositionRef.current[2] = pos.z;
    
    // Update animation state (throttled for React, but refs updated immediately)
    const direction = hasMovement 
      ? { x: normalizedX * magnitude, z: normalizedZ * magnitude }
      : animationStateRef.current.movementDirection;
    updateAnimationState(hasMovement, hasMovement && isSprinting, direction);
    
    // ========================================
    // CENTRALIZED INTERACTION TARGET RESOLVER
    // Priority: Talk > Stand > Sit > Door > Interact
    // Uses HYSTERESIS to prevent flashing:
    // - Enter interaction at radius R
    // - Exit interaction at radius R * 1.3
    // ========================================
    
    const HYSTERESIS_MULTIPLIER = 1.3;
    
    let bestInteraction = null;
    let bestPriority = -1;
    let bestDistance = Infinity;
    
    // Check if we should use extended radius (when already interacting with this target)
    const getCurrentRadius = (baseRadius, interactionId) => {
      if (currentInteraction.current?.id === interactionId) {
        return baseRadius * HYSTERESIS_MULTIPLIER;
      }
      return baseRadius;
    };
    
    // 1. Check for nearby REMOTE PLAYERS (Talk interaction) - Highest priority
    if (remotePlayers && remotePlayers.length > 0) {
      for (const player of remotePlayers) {
        if (!player.currentPosition) continue;
        
        const dx = pos.x - player.currentPosition.x;
        const dz = pos.z - player.currentPosition.z;
        const distance = Math.sqrt(dx * dx + dz * dz);
        
        const talkId = `talk-${player.playerId}`;
        const effectiveRadius = getCurrentRadius(TALK_RADIUS, talkId);
        
        if (distance < effectiveRadius) {
          const priority = INTERACTION_PRIORITY.talk;
          // Prefer closer player if multiple in range
          if (priority > bestPriority || (priority === bestPriority && distance < bestDistance)) {
            bestPriority = priority;
            bestDistance = distance;
            bestInteraction = {
              id: talkId,
              type: 'talk',
              priority: priority,
              prompt: `Talk to ${player.displayName || 'Player'}`,
              targetPlayer: {
                playerId: player.playerId,
                displayName: player.displayName || 'Player',
                username: player.username || '',
              },
              action: { type: 'talkToPlayer', playerId: player.playerId },
            };
          }
        }
      }
    }
    
    // 2. Check scene-defined interactions (doors, social zones) with hysteresis
    const sceneInteractions = INTERACTIONS[sceneId] || [];
    for (const interaction of sceneInteractions) {
      const dx = pos.x - interaction.position.x;
      const dz = pos.z - interaction.position.z;
      const distance = Math.sqrt(dx * dx + dz * dz);
      
      const effectiveRadius = getCurrentRadius(interaction.radius, interaction.id);
      
      if (distance < effectiveRadius) {
        const priority = interaction.priority || INTERACTION_PRIORITY.interact;
        // Select if higher priority, or same priority but closer
        if (priority > bestPriority || (priority === bestPriority && distance < bestDistance)) {
          bestPriority = priority;
          bestDistance = distance;
          bestInteraction = interaction;
        }
      }
    }
    
    // Track nearby players for chat proximity (separate from interaction)
    const nearbyPlayerIds = [];
    if (remotePlayers && remotePlayers.length > 0) {
      for (const player of remotePlayers) {
        if (!player.currentPosition) continue;
        const dx = pos.x - player.currentPosition.x;
        const dz = pos.z - player.currentPosition.z;
        const distance = Math.sqrt(dx * dx + dz * dz);
        if (distance < TALK_RADIUS * 2) { // Wider radius for chat awareness
          nearbyPlayerIds.push(player.playerId);
        }
      }
    }
    
    // Throttled position update for debug HUD and multiplayer
    const now = Date.now();
    if (now - lastPositionUpdate.current > 100) {
      lastPositionUpdate.current = now;
      if (onPositionUpdate) {
        // Calculate movement state
        const movementState = !hasMovement ? 'idle' : (isSprinting ? 'running' : 'walking');
        // Get character rotation from movement direction
        let playerRotation = 0;
        if (hasMovement && (normalizedX !== 0 || normalizedZ !== 0)) {
          playerRotation = Math.atan2(normalizedX * magnitude, normalizedZ * magnitude);
        }
        onPositionUpdate({ x: pos.x, y: pos.y, z: pos.z }, playerRotation, movementState);
      }
      // Update nearby players list (throttled)
      if (onNearbyPlayersChange) {
        onNearbyPlayersChange(nearbyPlayerIds);
      }
    }
    
    // Notify of interaction change (only if different)
    if (bestInteraction?.id !== currentInteraction.current?.id) {
      currentInteraction.current = bestInteraction;
      if (onInteractionChange) {
        onInteractionChange(bestInteraction);
      }
    }
    
    // Handle E key or mobile interact
    const interactNow = keys.current.e || mobileInput.interact;
    if (interactNow && !wasEPressed.current) {
      if (currentInteraction.current && onTriggerInteraction) {
        onTriggerInteraction(currentInteraction.current);
      }
    }
    wasEPressed.current = interactNow;
    
    // Update camera position (orbit around player)
    smoothTarget.current.lerp(new THREE.Vector3(pos.x, pos.y, pos.z), CAMERA_CONFIG_BASE.followLerp);
    
    // Smoothly interpolate camera distance
    cameraDistance.current += (targetCameraDistance.current - cameraDistance.current) * 0.1;
    const effectiveDistance = camConfig.isFirstPerson ? 0.1 : cameraDistance.current;
    
    const { pitch } = cameraOrbit.current;
    
    // First person mode: camera at eye level
    if (camConfig.isFirstPerson) {
      camera.position.set(
        smoothTarget.current.x,
        smoothTarget.current.y + camConfig.height,
        smoothTarget.current.z
      );
      // Look in direction of movement or forward
      const lookDist = 5;
      if (hasMovement) {
        camera.lookAt(
          smoothTarget.current.x + normalizedX * magnitude * lookDist,
          smoothTarget.current.y + camConfig.height,
          smoothTarget.current.z + normalizedZ * magnitude * lookDist
        );
      } else {
        // Look based on yaw
        camera.lookAt(
          smoothTarget.current.x + Math.sin(yaw) * lookDist,
          smoothTarget.current.y + camConfig.height,
          smoothTarget.current.z + Math.cos(yaw) * lookDist
        );
      }
    } else {
      // Third person camera
      const cameraOffset = new THREE.Vector3(
        Math.sin(yaw) * Math.cos(pitch) * effectiveDistance,
        Math.sin(pitch) * effectiveDistance + camConfig.height,
        Math.cos(yaw) * Math.cos(pitch) * effectiveDistance
      );
      
      const cameraTarget = smoothTarget.current.clone();
      cameraTarget.y += camConfig.lookAtOffset;
      
      const cameraPosition = cameraTarget.clone().add(cameraOffset);
      camera.position.lerp(cameraPosition, 0.1);
      camera.lookAt(cameraTarget);
    }
  });
  
  return (
    <>
      {/* Physics body (invisible) */}
      <RigidBody
        ref={rigidBodyRef}
        position={[initialPosition.x, initialPosition.y, initialPosition.z]}
        enabledRotations={[false, false, false]}
        linearDamping={10}
        type="dynamic"
        colliders={false}
      >
        <CapsuleCollider args={[0.5, 0.4]} position={[0, 0.9, 0]} />
      </RigidBody>
      
      {/* Visual character - uses ref-based position for smooth per-frame updates */}
      {/* Hide character in first-person mode */}
      {(gameSettings.cameraMode !== 'firstPerson') && (
        <LocalCharacterVisual
          positionRef={visualPositionRef}
          animationRef={animationStateRef}
        />
      )}
    </>
  );
}

/**
 * LocalCharacterVisual - Wrapper that updates character position from ref every frame
 * This ensures smooth visual movement independent of React state updates
 */
function LocalCharacterVisual({ positionRef, animationRef }) {
  const groupRef = useRef();
  
  // Update position from ref EVERY FRAME
  useFrame(() => {
    if (groupRef.current && positionRef.current) {
      groupRef.current.position.set(
        positionRef.current[0],
        positionRef.current[1],
        positionRef.current[2]
      );
    }
  });
  
  return (
    <group ref={groupRef}>
      <HumanoidCharacter
        position={[0, 0, 0]} // Position handled by parent group
        isMoving={animationRef.current?.isMoving || false}
        isRunning={animationRef.current?.isRunning || false}
        movementDirection={animationRef.current?.movementDirection || { x: 0, z: 0 }}
      />
    </group>
  );
}

// ========================
// MAIN GAME WORLD COMPONENT
// ========================

export default function EnhancedGameWorld() {
  const navigate = useNavigate();
  const { settlementId, locationId } = useParams();
  const [searchParams] = useSearchParams();
  const { user, token } = useAuth();
  
  // Game state
  // Phase 3: Use 'midgard-world' for exterior, 'golden-mead-hall' for interior
  const [currentSceneId, setCurrentSceneId] = useState('midgard-world');
  const [spawnPosition, setSpawnPosition] = useState(SPAWN_POINTS['midgard-world'].default);
  const [travelMethod, setTravelMethod] = useState('walk');
  const [loaded, setLoaded] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionTarget, setTransitionTarget] = useState('');
  const [currentInteraction, setCurrentInteraction] = useState(null);
  const isMobile = useIsMobile(); // Use hook for mobile detection
  const [debugPosition, setDebugPosition] = useState({ x: 0, y: 0, z: 0 });
  const [showDebug, setShowDebug] = useState(false); // Debug HUD disabled by default
  
  // Input blocking state for menus/chat
  const isInputBlocked = useIsInputBlocked();
  const isChatFocused = useInputState(state => state.isChatFocused);
  
  // Game menu state
  const [showGameMenu, setShowGameMenu] = useState(false);
  const [currentWaypoint, setCurrentWaypoint] = useState(null);

  // Use menu input capture for game menu (must run after showGameMenu exists)
  useMenuInputCapture('gameMenu', showGameMenu);
  
  // Phase 3: World state tracking
  const [isInWorldMode, setIsInWorldMode] = useState(true);  // true = outdoor world, false = interior scene

  // Phase 6E: active property dwelling session (owned_property_id + persistent
  // interior_instance_id) and its loaded furniture, plus any entry error.
  const [propertyEntry, setPropertyEntry] = useState(null);
  const [interiorFurniture, setInteriorFurniture] = useState([]);
  const [propertyError, setPropertyError] = useState(null);
  
  // Tutorial state
  const [showTutorial, setShowTutorial] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false); // Track if user has interacted yet
  
  // Social zone state
  const [isSeated, setIsSeated] = useState(false);
  const [currentSeatInfo, setCurrentSeatInfo] = useState(null);
  const [sittingInProgress, setSittingInProgress] = useState(false);
  
  // Chat state
  const [chatOpen, setChatOpen] = useState(false);
  const [localMessages, setLocalMessages] = useState([]);
  const [zoneMessages, setZoneMessages] = useState({});
  const [directMessages, setDirectMessages] = useState({});
  const [directTarget, setDirectTarget] = useState(null);
  const [nearbyPlayers, setNearbyPlayers] = useState([]);
  
  // Performance & Settings state
  const [showPerfStats, setShowPerfStats] = useState(() => localStorage.getItem('midgard_show_perf_stats') === 'true');
  const [perfStats, setPerfStats] = useState(null);
  const [graphicsProfile, setGraphicsProfile] = useState(getSavedGraphicsProfile);
  const [showSettings, setShowSettings] = useState(false);
  const [gameSettings, setGameSettings] = useState(loadSettings);
  const { rate: wsMessageRate, incrementCount: incrementWsCount } = useWsMessageRate();
  
  // Use menu input capture for settings modal
  useMenuInputCapture('settings', showSettings);
  
  // Compute actual graphics settings
  const graphicsSettings = useMemo(() => {
    const profile = gameSettings.graphicsProfile || graphicsProfile;
    if (profile === 'auto') {
      return getAutoProfile();
    }
    return GRAPHICS_PROFILES[profile];
  }, [gameSettings.graphicsProfile, graphicsProfile]);
  
  // Handle settings change
  const handleSettingsChange = useCallback((newSettings) => {
    setGameSettings(newSettings);
    setShowPerfStats(newSettings.showPerfStats);
    setGraphicsProfile(newSettings.graphicsProfile);
  }, []);
  
  // Show tutorial on first visit (after initial load)
  useEffect(() => {
    if (loaded && !hasSeenTutorial()) {
      // Small delay to let the world render first
      const timer = setTimeout(() => setShowTutorial(true), 500);
      return () => clearTimeout(timer);
    }
  }, [loaded]);
  
  // Multiplayer state
  const [connectionState, setConnectionState] = useState(ConnectionState.DISCONNECTED);
  const [remotePlayers, setRemotePlayers] = useState([]);
  const localPlayerRotation = useRef(0);
  const localMovementState = useRef('idle');
  
  // Chat and voice state (now managed by GameInputManager)
  const setChatFocused = useInputState(state => state.setChatFocused);
  
  // Initialize scene based on URL params
  // Phase 3: Default to world mode for exterior
  useEffect(() => {
    const travel = searchParams.get('travel') || 'walk';
    setTravelMethod(travel);
    
    if (locationId === 'thornhaven-tavern') {
      // Fast travel to tavern interior
      setCurrentSceneId('golden-mead-hall');
      setIsInWorldMode(false);
      const sp = travel === 'fastTravel' 
        ? SPAWN_POINTS['golden-mead-hall'].fastTravel 
        : SPAWN_POINTS['golden-mead-hall'].entrance;
      setSpawnPosition(sp);
    } else {
      // Default to Midgard world exterior
      setCurrentSceneId('midgard-world');
      setIsInWorldMode(true);
      // Phase 6E: optional dev spawn override to land right at the Small House
      // doorway (?spawn=smallhouse). Does not affect normal play.
      const spawnParam = searchParams.get('spawn');
      let sp;
      if (spawnParam === 'smallhouse') {
        sp = SPAWN_POINTS['midgard-world'].smallHouseExit;
      } else {
        sp = travel === 'fastTravel'
          ? SPAWN_POINTS['midgard-world'].fastTravel
          : SPAWN_POINTS['midgard-world'].default;
      }
      setSpawnPosition(sp);
    }
  }, [settlementId, locationId, searchParams]);
  
  // Multiplayer connection lifecycle
  const mountedRef = useRef(false);
  const connectTimeoutRef = useRef(null);
  
  useEffect(() => {
    if (!token || !currentSceneId) return;
    
    // Debounce connection to handle React StrictMode double-mount
    if (connectTimeoutRef.current) {
      clearTimeout(connectTimeoutRef.current);
    }
    
    connectTimeoutRef.current = setTimeout(() => {
      if (!mountedRef.current) {
        mountedRef.current = true;
        
        // Set up event handlers
        multiplayerService.onConnectionStateChange = (state) => {
          setConnectionState(state);
        };
        
        multiplayerService.onRemotePlayersUpdate = (players) => {
          setRemotePlayers([...players]);
        };
        
        multiplayerService.onPlayerJoined = (player) => {
          console.log(`Player joined: ${player.displayName}`);
        };
        
        multiplayerService.onPlayerLeft = (player) => {
          console.log(`Player left: ${player.playerId}`);
          // Remove from direct target if they were being talked to
          setDirectTarget(prev => prev?.playerId === player.playerId ? null : prev);
        };
        
        multiplayerService.onError = (error) => {
          console.error('Multiplayer error:', error);
        };
        
        // Chat message handler
        multiplayerService.onChatMessage = (message) => {
          console.log('Chat received:', message.chatType, message.senderName);
          
          switch (message.chatType) {
            case 'local':
              setLocalMessages(prev => [...prev.slice(-49), message]);
              break;
            case 'zone':
              setZoneMessages(prev => ({
                ...prev,
                [message.scopeId]: [...(prev[message.scopeId] || []).slice(-49), message],
              }));
              break;
            case 'direct':
              // For direct messages, key by the OTHER player's ID
              const otherPlayerId = message.senderId === multiplayerService.playerId 
                ? message.scopeId 
                : message.senderId;
              setDirectMessages(prev => ({
                ...prev,
                [otherPlayerId]: [...(prev[otherPlayerId] || []).slice(-49), message],
              }));
              break;
            default:
              break;
          }
        };
        
        // Conversation started handler
        multiplayerService.onConversationStarted = (data) => {
          console.log('Conversation started with:', data.displayName);
          setDirectTarget({
            playerId: data.playerId,
            displayName: data.displayName,
            username: data.username,
          });
        };
        
        // Connect to multiplayer
        // Phase 3: Connect to world or scene based on mode
        if (isInWorldMode) {
          multiplayerService.connect(token, null, spawnPosition, {
            worldId: 'midgard',
            regionId: 'central-plains',
          });
        } else {
          multiplayerService.connect(token, currentSceneId, spawnPosition);
        }
      }
    }, 100); // Small delay to debounce StrictMode double-mount
    
    // Cleanup on unmount
    return () => {
      if (connectTimeoutRef.current) {
        clearTimeout(connectTimeoutRef.current);
      }
      // Only disconnect if we actually connected
      if (mountedRef.current) {
        mountedRef.current = false;
        multiplayerService.disconnect();
      }
    };
  }, [token]); // Only reconnect if token changes (login/logout)
  
  // Handle scene/world changes for multiplayer (after initial connection)
  const sceneChangeRef = useRef(currentSceneId);
  const worldModeRef = useRef(isInWorldMode);
  useEffect(() => {
    // Skip if nothing changed
    if (sceneChangeRef.current === currentSceneId && worldModeRef.current === isInWorldMode) return;
    sceneChangeRef.current = currentSceneId;
    worldModeRef.current = isInWorldMode;
    
    if (connectionState === ConnectionState.CONNECTED) {
      // Phase 3: Change to world or scene based on mode
      if (isInWorldMode) {
        multiplayerService.changeWorld(spawnPosition, 'central-plains');
      } else if (currentSceneId) {
        multiplayerService.changeScene(currentSceneId, spawnPosition);
      }
    }
  }, [currentSceneId, isInWorldMode, spawnPosition, connectionState]);
  
  // Remote player interpolation - FIXED: Only update React state when players actually change
  // Previously, setting state every frame caused constant re-renders that broke the 3D scene
  useEffect(() => {
    let animationFrameId;
    let lastTime = performance.now();
    let lastPlayerCount = 0;
    let lastPositionHash = '';
    
    const interpolate = () => {
      const now = performance.now();
      const deltaTime = (now - lastTime) / 1000;
      lastTime = now;
      
      // Interpolate positions (mutates internal state only, no React re-render)
      multiplayerService.interpolateRemotePlayers(deltaTime);
      
      // Check if we actually need to update React state
      const currentPlayers = multiplayerService.getRemotePlayers();
      const currentCount = currentPlayers.length;
      
      // Create a hash of positions (rounded to reduce update frequency)
      const positionHash = currentPlayers.map(p => 
        `${p.playerId}:${Math.round((p.currentPosition?.x || 0) * 2) / 2},${Math.round((p.currentPosition?.z || 0) * 2) / 2}`
      ).join('|');
      
      // Only trigger React re-render when something meaningful changed
      if (currentCount !== lastPlayerCount || positionHash !== lastPositionHash) {
        lastPlayerCount = currentCount;
        lastPositionHash = positionHash;
        setRemotePlayers([...currentPlayers]);
      }
      
      animationFrameId = requestAnimationFrame(interpolate);
    };
    
    if (connectionState === ConnectionState.CONNECTED) {
      animationFrameId = requestAnimationFrame(interpolate);
    }
    
    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [connectionState]);
  
  const handleExit = useCallback(() => {
    // Exit to settlement page (not regional map) for proper navigation hierarchy
    navigate('/world/midgard/settlement/thornhaven');
  }, [navigate]);
  
  const handleTriggerInteraction = useCallback(async (interaction) => {
    if (!interaction?.action) return;
    
    // Track that user has performed an interaction (hides contextual hint)
    setHasInteracted(true);
    
    const { action } = interaction;
    
    // Handle "Talk to Player" action
    if (action.type === 'talkToPlayer') {
      const targetPlayer = interaction.targetPlayer;
      if (!targetPlayer) return;
      
      // Start conversation via multiplayer service
      multiplayerService.startConversation(targetPlayer.playerId);
      setDirectTarget(targetPlayer);
      setChatOpen(true);
      return;
    }
    
    // Handle social zone sit/stand actions
    if (action.type === 'sitAtZone') {
      if (sittingInProgress) return;
      
      try {
        setSittingInProgress(true);
        const result = await sitAtZone(currentSceneId, action.zoneId);
        
        if (result.success) {
          setIsSeated(true);
          setCurrentSeatInfo({
            zoneId: action.zoneId,
            zoneLabel: interaction.zoneLabel || getZoneTypeLabel(result.zone?.type) || 'Zone',
            seatId: result.seat.id,
            position: result.seat.position,
            rotation: result.seat.rotation,
            zone: result.zone,
          });
          emitJoinZone(action.zoneId, result.zone);
          // Notify WebSocket so other players see us seated AND for zone chat routing
          multiplayerService.notifySit(action.zoneId, result.seat.id, result.seat.position);
          setCurrentInteraction(null);
        }
      } catch (err) {
        console.error('Failed to sit:', err);
      } finally {
        setSittingInProgress(false);
      }
      return;
    }
    
    if (action.type === 'stand') {
      if (sittingInProgress) return;
      
      try {
        setSittingInProgress(true);
        const previousZoneId = currentSeatInfo?.zoneId;
        const result = await standUp();
        
        if (result.success) {
          setIsSeated(false);
          if (previousZoneId) {
            emitLeaveZone(previousZoneId);
            // Notify WebSocket so zone chat routing is updated
            multiplayerService.notifyStand(previousZoneId);
          }
          setCurrentSeatInfo(null);
          setCurrentInteraction(null);
        }
      } catch (err) {
        console.error('Failed to stand:', err);
      } finally {
        setSittingInProgress(false);
      }
      return;
    }
    
    // Phase 6E: enter the first functional dwelling via the property backend.
    if (action.type === 'enterProperty') {
      if (isSeated && currentSeatInfo) {
        const prevZone = currentSeatInfo.zoneId;
        await standUp().catch(() => {});
        setIsSeated(false);
        if (prevZone) emitLeaveZone(prevZone);
        setCurrentSeatInfo(null);
      }
      setPropertyError(null);
      setIsTransitioning(true);
      setTransitionTarget(SCENES[action.targetScene]?.name || 'Dwelling');
      try {
        const def = action.definitionId;
        let entry;
        try {
          // Canonical entry first
          entry = (await api.post('/properties/enter', { definition_id: def })).data;
        } catch (err) {
          const status = err?.response?.status;
          if (status === 403 || status === 404) {
            // First-time owner: free claim, then enter canonically
            await api.post('/properties/claim', { definition_id: def });
            entry = (await api.post('/properties/enter', { definition_id: def })).data;
          } else {
            throw err;
          }
        }
        // Load placed furniture from Phase 6D backend (empty for a fresh property)
        let furniture = [];
        try {
          furniture = (await api.get(`/properties/${entry.owned_property_id}/furniture`)).data || [];
        } catch (e) {
          furniture = [];
        }
        const sp = entry.interior_spawn?.position || { x: 0, y: 1, z: 2 };
        setPropertyEntry({
          owned_property_id: entry.owned_property_id,
          interior_instance_id: entry.interior_instance_id,
          definition_id: entry.definition_id,
        });
        setInteriorFurniture(furniture);
        setTimeout(() => {
          setCurrentSceneId(action.targetScene);
          setIsInWorldMode(false);
          setSpawnPosition({ x: sp.x ?? 0, y: sp.y ?? 1, z: sp.z ?? 2 });
          setTravelMethod('walk');
          setCurrentInteraction(null);
          setIsTransitioning(false);
        }, 600);
      } catch (err) {
        console.error('enterProperty failed:', err);
        setPropertyError(err?.response?.data?.detail || 'Could not enter this dwelling.');
        setIsTransitioning(false);
      }
      return;
    }

    // Phase 6E: exit the dwelling back to its canonical exterior doorway.
    if (action.type === 'exitProperty') {
      setIsTransitioning(true);
      setTransitionTarget('Thornhaven');
      try {
        await api.post('/properties/exit', {
          interior_instance_id: propertyEntry?.interior_instance_id,
          owned_property_id: propertyEntry?.owned_property_id,
        });
      } catch (err) {
        console.error('exitProperty failed (returning to world anyway):', err);
      }
      setTimeout(() => {
        setCurrentSceneId('midgard-world');
        setIsInWorldMode(true);
        setSpawnPosition(SPAWN_POINTS['midgard-world'].smallHouseExit);
        setTravelMethod('walk');
        setPropertyEntry(null);
        setInteriorFurniture([]);
        setCurrentInteraction(null);
        setIsTransitioning(false);
      }, 600);
      return;
    }

    // Handle scene transition actions
    if (action.type === 'enterScene' || action.type === 'exitScene' || action.type === 'exitToWorld') {
      // Auto-stand if seated when transitioning
      if (isSeated && currentSeatInfo) {
        const previousZoneId = currentSeatInfo.zoneId;
        await standUp().catch(() => {});
        setIsSeated(false);
        if (previousZoneId) emitLeaveZone(previousZoneId);
        setCurrentSeatInfo(null);
      }
      
      setIsTransitioning(true);
      
      // Phase 3: Handle exitToWorld action
      if (action.type === 'exitToWorld') {
        setTransitionTarget('Midgard');
        
        setTimeout(() => {
          setCurrentSceneId('midgard-world');
          setIsInWorldMode(true);
          const sp = SPAWN_POINTS['midgard-world'][action.spawnPoint] || SPAWN_POINTS['midgard-world'].default;
          setSpawnPosition(sp);
          setTravelMethod('walk');
          setCurrentInteraction(null);
          setIsTransitioning(false);
        }, 600);
      } else {
        // Interior scene transition
        setTransitionTarget(SCENES[action.targetScene]?.name || '...');
        
        setTimeout(() => {
          setCurrentSceneId(action.targetScene);
          setIsInWorldMode(false);  // Entering interior
          setSpawnPosition(SPAWN_POINTS[action.targetScene][action.spawnPoint]);
          setTravelMethod(action.type === 'enterScene' ? 'walk' : 'walk');
          setCurrentInteraction(null);
          setIsTransitioning(false);
        }, 600);
      }
    }
  }, [currentSceneId, isSeated, currentSeatInfo, sittingInProgress, propertyEntry]);
  
  // Handle waypoint set/clear for game menu
  const handleSetWaypoint = useCallback((position) => {
    setCurrentWaypoint(position);
  }, []);
  
  const handleClearWaypoint = useCallback(() => {
    setCurrentWaypoint(null);
  }, []);
  
  // ========================
  // CHAT HANDLERS
  // ========================
  
  const handleSendLocalChat = useCallback((content) => {
    if (connectionState !== ConnectionState.CONNECTED) return;
    multiplayerService.sendLocalChat(content);
  }, [connectionState]);
  
  const handleSendZoneChat = useCallback((content) => {
    if (connectionState !== ConnectionState.CONNECTED || !isSeated || !currentSeatInfo?.zoneId) return;
    multiplayerService.sendZoneChat(content);
  }, [connectionState, isSeated, currentSeatInfo]);
  
  const handleSendDirectChat = useCallback((recipientId, content) => {
    if (connectionState !== ConnectionState.CONNECTED || !recipientId) return;
    multiplayerService.sendDirectChat(recipientId, content);
  }, [connectionState]);
  
  const handleCloseDirectChat = useCallback(() => {
    if (directTarget?.playerId) {
      multiplayerService.endConversation(directTarget.playerId);
    }
    setDirectTarget(null);
  }, [directTarget]);
  
  const handleNearbyPlayersChange = useCallback((playerIds) => {
    setNearbyPlayers(playerIds);
  }, []);
  
  const handlePositionUpdate = useCallback((pos, rotation, movementState) => {
    // Phase 3: Throttle debug position updates to ~4Hz for chunk streaming (chunks are 64u wide)
    // This prevents 60Hz React re-renders of the entire component tree
    const now = performance.now();
    if (!handlePositionUpdate._lastUpdate || now - handlePositionUpdate._lastUpdate > 250) {
      handlePositionUpdate._lastUpdate = now;
      setDebugPosition(pos);
    }
    
    // Track local state for multiplayer
    if (rotation !== undefined) {
      localPlayerRotation.current = rotation;
    }
    if (movementState !== undefined) {
      localMovementState.current = movementState;
    }
    
    // Send to multiplayer service
    multiplayerService.updateLocalPlayer({
      position: pos,
      rotation: localPlayerRotation.current,
      movementState: localMovementState.current,
    });
  }, []);
  
  // Calculate distance to nearest interaction for debug display
  const sceneInteractions = INTERACTIONS[currentSceneId] || [];
  const nearestInteractionDebug = sceneInteractions.length > 0 ? sceneInteractions[0] : null;
  const distanceToInteraction = nearestInteractionDebug ? 
    Math.sqrt(
      Math.pow(debugPosition.x - nearestInteractionDebug.position.x, 2) + 
      Math.pow(debugPosition.z - nearestInteractionDebug.position.z, 2)
    ) : 0;
  
  // When seated, override interaction to show "Stand Up"
  useEffect(() => {
    if (isSeated && currentSeatInfo) {
      setCurrentInteraction({
        id: 'stand-up',
        type: 'socialZone',
        priority: INTERACTION_PRIORITY.stand,
        prompt: 'Stand Up',
        action: { type: 'stand' },
      });
    }
  }, [isSeated, currentSeatInfo]);
  
  const currentScene = SCENES[currentSceneId];
  
  // Phase 3: Scene component selection - world exterior or interior scene
  // For world mode, render Thornhaven settlement + ChunkStreamer for roads/landmarks
  const SceneComponent = useMemo(() => {
    if (currentSceneId === 'golden-mead-hall') {
      return GoldenMeadHallInterior;
    }
    if (currentSceneId === 'thornhaven-small-house') {
      return null; // Phase 6E: rendered separately so it can receive furniture props
    }
    // World exterior (midgard-world or legacy thornhaven-exterior)
    return ThornhavenScene;
  }, [currentSceneId]);
  
  // Additional world content (roads, landmarks) rendered when in world mode
  const renderWorldContent = isInWorldMode;
  
  return (
    <div className="fixed inset-0 bg-black" data-testid="game-world">
      {/* Phase 6E: dwelling entry error toast */}
      {propertyError && (
        <div
          className="absolute top-20 left-1/2 -translate-x-1/2 z-[70] px-4 py-2 rounded-lg bg-red-900/90 text-red-100 text-sm font-medium border border-red-500/50 cursor-pointer"
          onClick={() => setPropertyError(null)}
          data-testid="property-error-toast"
        >
          {propertyError}
        </div>
      )}
      {/* Loading */}
      {!loaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-black z-50">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-amber-900/30 border-t-amber-500 rounded-full animate-spin mx-auto mb-4" />
            <p className="text-amber-400 text-lg">Loading world...</p>
          </div>
        </div>
      )}
      
      {/* Transition overlay */}
      {isTransitioning && (
        <div className="absolute inset-0 flex items-center justify-center bg-black z-50 animate-pulse">
          <div className="text-center">
            <MapPin className="w-12 h-12 text-amber-500 mx-auto mb-4 animate-bounce" />
            <p className="text-amber-400 text-lg">Traveling to {transitionTarget}...</p>
          </div>
        </div>
      )}
      
      {/* HUD */}
      <div className="absolute top-0 left-0 right-0 z-40 p-4 flex items-center justify-between pointer-events-none">
        <button
          onClick={handleExit}
          className="flex items-center gap-2 px-4 py-2 rounded-full bg-black/50 backdrop-blur-sm border border-amber-900/30 hover:border-amber-500/50 transition-all pointer-events-auto"
          data-testid="exit-game-btn"
        >
          <ArrowLeft className="w-5 h-5 text-amber-100" />
          <span className="text-amber-100">Exit</span>
        </button>
        
        <div className="px-4 py-2 rounded-full bg-black/50 backdrop-blur-sm border border-amber-900/30">
          <span className="text-amber-100 text-sm">{currentScene?.name}</span>
          {currentSceneId === 'golden-mead-hall' && (
            <span className="text-amber-500 text-xs ml-2">(inside)</span>
          )}
        </div>
        
        {/* Multiplayer connection status */}
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full backdrop-blur-sm border ${
          connectionState === ConnectionState.CONNECTED ? 'bg-emerald-900/40 border-emerald-600/30' :
          connectionState === ConnectionState.RECONNECTING ? 'bg-amber-900/40 border-amber-600/30' :
          'bg-red-900/40 border-red-600/30'
        }`} data-testid="multiplayer-status">
          {connectionState === ConnectionState.CONNECTED && (
            <>
              <Wifi className="w-4 h-4 text-emerald-400" />
              <span className="text-emerald-300 text-xs">Connected</span>
              {remotePlayers.length > 0 && (
                <span className="text-emerald-400/70 text-xs">({remotePlayers.length})</span>
              )}
            </>
          )}
          {connectionState === ConnectionState.RECONNECTING && (
            <>
              <Loader className="w-4 h-4 text-amber-400 animate-spin" />
              <span className="text-amber-300 text-xs">Reconnecting...</span>
            </>
          )}
          {(connectionState === ConnectionState.DISCONNECTED || connectionState === ConnectionState.CONNECTING || connectionState === ConnectionState.AUTHENTICATING) && (
            <>
              {connectionState === ConnectionState.DISCONNECTED ? (
                <WifiOff className="w-4 h-4 text-red-400" />
              ) : (
                <Loader className="w-4 h-4 text-amber-400 animate-spin" />
              )}
              <span className="text-gray-300 text-xs">
                {connectionState === ConnectionState.DISCONNECTED ? 'Offline' : 'Connecting...'}
              </span>
            </>
          )}
        </div>
      </div>
      
      {/* First-time tutorial overlay */}
      {showTutorial && (
        <GameTutorial 
          onClose={() => setShowTutorial(false)} 
          isMobile={isMobile}
        />
      )}
      
      {/* Contextual hint for first interaction */}
      <ContextualHint visible={!hasInteracted && currentInteraction && !showTutorial && !isInputBlocked}>
        {isMobile ? 'Tap Interact' : 'Press E to interact'}
      </ContextualHint>
      
      {/* DEBUG HUD - Remove after verification */}
      {showDebug && (
        <div className="absolute top-20 right-4 z-40 p-3 rounded-lg bg-red-900/80 border border-red-500/50 text-xs font-mono pointer-events-none" data-testid="debug-hud">
          <p className="text-red-200 font-bold mb-1">DEBUG</p>
          <p className="text-white">Player: ({debugPosition.x.toFixed(1)}, {debugPosition.z.toFixed(1)})</p>
          {nearestInteractionDebug && (
            <>
              <p className="text-yellow-300">Target: ({nearestInteractionDebug.position.x}, {nearestInteractionDebug.position.z})</p>
              <p className={`${distanceToInteraction <= nearestInteractionDebug.radius ? 'text-green-400' : 'text-white'}`}>
                Distance: {distanceToInteraction.toFixed(2)} / {nearestInteractionDebug.radius}
              </p>
              <p className="text-gray-400">{nearestInteractionDebug.id}</p>
            </>
          )}
          {currentInteraction && <p className="text-green-400 font-bold mt-1">IN RANGE!</p>}
        </div>
      )}
      
      {/* Mobile controls - NORSE-STYLED ECCTRL CONTROLS */}
      {isMobile && (
        <NorseMobileControls
          currentInteraction={currentInteraction}
          onInteract={handleTriggerInteraction}
          showPTT={false} // PTT handled by VoiceControls
          disabled={isInputBlocked}
        />
      )}
      
      {/* Desktop interaction prompt */}
      {currentInteraction && !isMobile && !isInputBlocked && (
        <div className="absolute bottom-24 left-1/2 -translate-x-1/2 z-40 pointer-events-none">
          <div className="px-6 py-3 rounded-xl bg-black/80 backdrop-blur-sm border border-amber-500/50 flex items-center gap-3 animate-pulse">
            <span className="w-8 h-8 rounded-lg bg-amber-500/20 border border-amber-500 flex items-center justify-center text-amber-400 font-bold text-sm">E</span>
            <span className="text-amber-100 font-medium">{currentInteraction.prompt}</span>
          </div>
        </div>
      )}
      
      {/* Minimap - Top right corner */}
      <div 
        className="fixed z-40 pointer-events-none"
        style={{
          top: 'calc(env(safe-area-inset-top, 0px) + 56px)',
          right: 'calc(env(safe-area-inset-right, 0px) + 56px)',
        }}
      >
        <Minimap
          playerPosition={debugPosition}
          playerRotation={localPlayerRotation.current}
          remotePlayers={remotePlayers}
          waypoint={currentWaypoint}
        />
      </div>
      
      {/* Game Menu Dock */}
      <GameMenu
        isOpen={showGameMenu}
        onToggle={() => setShowGameMenu(prev => !prev)}
        currentWaypoint={currentWaypoint}
        onSetWaypoint={handleSetWaypoint}
        onClearWaypoint={handleClearWaypoint}
        onOpenSettings={() => setShowSettings(true)}
        playerPosition={debugPosition}
      />
      
      {/* Chat UI */}
      <GameChat
        localMessages={localMessages}
        zoneMessages={zoneMessages[currentSeatInfo?.zoneId] || []}
        directMessages={directTarget ? (directMessages[directTarget.playerId] || []) : []}
        onSendLocal={handleSendLocalChat}
        onSendZone={handleSendZoneChat}
        onSendDirect={handleSendDirectChat}
        currentPlayerId={multiplayerService.playerId}
        isSeated={isSeated}
        currentZoneId={currentSeatInfo?.zoneId}
        currentZoneLabel={currentSeatInfo?.zoneLabel}
        directTarget={directTarget}
        onCloseDirectChat={handleCloseDirectChat}
        isMobile={isMobile}
        onChatFocusChange={setChatFocused}
      />
      
      {/* Voice Controls - PTT ALWAYS visible while in 3D world */}
      <VoiceControls
        sceneId={currentSceneId}
        worldId={isInWorldMode ? 'midgard' : null}
        seatedZoneId={currentSeatInfo?.zoneId}
        activeConversation={directTarget ? {
          conversationId: directTarget.conversationId || directTarget.playerId,
          targetDisplayName: directTarget.displayName,
        } : null}
        gameSettings={gameSettings}
        isChatFocused={isChatFocused}
        nearbyPlayers={remotePlayers}
        onOpenSettings={() => setShowSettings(true)}
        isMobile={isMobile}
      />
      
      {/* Performance Overlay */}
      <PerformanceOverlayUI
        stats={perfStats}
        remotePlayers={remotePlayers.length}
        wsMessagesPerSec={wsMessageRate}
        visible={showPerfStats}
      />
      
      {/* Admin World Builder (tablet-first) - only the designated builder account */}
      {user?.email && user.email.toLowerCase() === WORLD_BUILDER_EMAIL && (
        <WorldBuilderPanel />
      )}

      {/* Settings Button - high z-index to ensure clickability */}
      <button
        onClick={() => setShowSettings(true)}
        onTouchEnd={(e) => { e.preventDefault(); setShowSettings(true); }}
        className="fixed top-14 right-4 z-[60] p-3 rounded-lg bg-black/80 backdrop-blur-sm border border-white/20 text-white hover:bg-black transition-colors pointer-events-auto"
        style={{ touchAction: 'manipulation' }}
        data-testid="settings-btn"
      >
        <Settings className="w-6 h-6" />
      </button>
      
      {/* Settings Modal */}
      <GameSettings
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        settings={gameSettings}
        onSettingsChange={handleSettingsChange}
        onShowTutorial={() => setShowTutorial(true)}
      />
      
      {/* 3D Canvas */}
      <Canvas
        key={`${currentSceneId}-${graphicsProfile}`} // Force remount on scene or profile change
        shadows={graphicsSettings.shadowEnabled}
        dpr={graphicsSettings.dpr}
        gl={{ 
          antialias: graphicsSettings.antialias, 
          alpha: false,
          powerPreference: 'high-performance',
        }}
        onCreated={({ gl }) => {
          // Use BasicShadowMap for better performance (PCFSoftShadowMap removed in newer Three.js)
          if (graphicsSettings.shadowEnabled) {
            gl.shadowMap.type = THREE.PCFShadowMap;
          }
          setLoaded(true);
        }}
      >
        {/* KeyboardControls provider for ecctrl. Map identity must stay stable —
            swapping it rebuilds the store and remounts the whole physics tree.
            Input is gated via EcctrlPlayer's `blocked` flag instead. */}
        <KeyboardControls map={KEYBOARD_MAP}>
          <Suspense fallback={null}>
            {/* Performance stats collector */}
            {showPerfStats && <PerformanceStatsCollector onStats={setPerfStats} />}
            
            <Physics gravity={[0, -20, 0]} debug={false}>
              {SceneComponent && <SceneComponent />}

              {/* Phase 6E: first functional dwelling */}
              {currentSceneId === 'thornhaven-small-house' && (
                <SmallHouseInterior furniture={interiorFurniture} />
              )}
              {renderWorldContent && <ThornhavenPerimeter />}
              {renderWorldContent && <ThornhavenTownSquare />}
              {renderWorldContent && <WorldObjectsLayer />}

              {/* Phase 3: Render chunk content when in world mode */}
              {renderWorldContent && (
                <ChunkStreamer playerPosition={debugPosition} />
              )}
              
              {/* LOCAL PLAYER - ecctrl-based controller */}
              <EcctrlPlayer
                initialPosition={spawnPosition}
                sceneId={currentSceneId}
                onInteractionChange={setCurrentInteraction}
                onTriggerInteraction={handleTriggerInteraction}
                onPositionUpdate={handlePositionUpdate}
                onNearbyPlayersChange={handleNearbyPlayersChange}
                isSeated={isSeated}
                seatPosition={currentSeatInfo?.position}
                seatRotation={currentSeatInfo?.rotation || 0}
                remotePlayers={remotePlayers}
                gameSettings={gameSettings}
                interactions={sceneInteractions}
                menuOpen={isInputBlocked}
                isChatFocused={isChatFocused}
              />
              
              {/* Remote players (no physics) - STRICT filtering */}
              {/* Phase 3: Filter by worldId for exterior, sceneId for interior */}
              <RemotePlayersContainer 
                remotePlayers={remotePlayers.filter(p => {
                  if (isInWorldMode) {
                    // In world mode: show players also in world (worldId match)
                    return p.worldId === 'midgard';
                  } else {
                    // In interior: show players in same scene
                    return p.sceneId && p.sceneId === currentSceneId;
                  }
                })} 
              />
            </Physics>
          </Suspense>
        </KeyboardControls>
      </Canvas>
    </div>
  );
}
